import { useMemo } from 'react'

import { Select, Space } from '@procurify/ui'
import { useQuery } from 'react-query'

import { BridgeLink } from '@webapp/core/components/BridgeLink'
import { useAppRoutes, useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { CreditCardAccount } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import { creditCardAccountQueries } from '@webapp/modules/accounts-payable/query-configs/creditCardAccountQueries'
import messages from './messages'

const LEGACY_CREDIT_CARD_SETTINGS_URL = '/#/settings/financials/creditcards'

export interface ICreditCardSelectProps {
  /** Excludes the current card from the target options (BHV-025) */
  excludeAccountId?: number
  value: Nullable<CreditCardAccount>
  onChange: (account: Nullable<CreditCardAccount>) => void
  isDisabled?: boolean
}

/**
 * Self-contained credit-card option select (ADR-3 shared component, not a
 * feature). Fetches its own options from GET /api/v2/ap/creditcards/.
 */
export const CreditCardSelect = ({
  excludeAccountId,
  value,
  onChange,
  isDisabled = false,
}: ICreditCardSelectProps) => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()
  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  // Setup link target when no target cards exist (BHV-025); PSERV_346:
  // flag-off keeps it on the Angular page.
  const creditCardSettingsUrl = PSERV_346_BASE_MIGRATION
    ? url('ManagePaymentAccountsPage', { status: 'creditcards' })
    : LEGACY_CREDIT_CARD_SETTINGS_URL

  const { data, isLoading } = useQuery(
    creditCardAccountQueries.list({ page: 1, pageSize: 9999 })
  )

  const options = useMemo(
    () =>
      (data?.accounts ?? [])
        .map((constructor) => new CreditCardAccount(constructor))
        .filter((account) => account.id !== excludeAccountId),
    [data, excludeAccountId]
  )

  const hasNoOptions = !isLoading && options.length === 0

  return (
    <Space size='small'>
      <Select<CreditCardAccount>
        options={options}
        value={value}
        isLoading={isLoading}
        isDisabled={isDisabled || hasNoOptions}
        getOptionLabel={(account) => account.name}
        getOptionValue={(account) => account.id.toString()}
        placeholder={
          hasNoOptions
            ? intl.formatMessage(messages.noCardsAvailablePlaceholder)
            : intl.formatMessage(messages.placeholder)
        }
        aria-label={intl.formatMessage(messages.ariaLabel)}
        onChange={(option: Nullable<CreditCardAccount>) => onChange(option)}
      />
      {hasNoOptions && (
        <BridgeLink to={creditCardSettingsUrl}>
          {intl.formatMessage(messages.setupLink)}
        </BridgeLink>
      )}
    </Space>
  )
}

CreditCardSelect.displayName = 'CreditCardSelect'
